import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useListB2BClaudeActivationsQuery } from '../app/api';
import UsageTierChart, { TierBadge, USAGE_TIERS, matchesTier } from './UsageTierChart';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

// Combined portal + Claude-seat status for one member — "Removed" wins over
// "Active"/"Pending" since a deactivated portal account or a rejected/revoked
// seat both mean the person shouldn't have Claude access anymore.
function memberStatus(row) {
  if (row.account_status === 'DEACTIVATED' || ['REJECTED', 'REVOKED'].includes(row.provision_status)) return 'removed';
  if (['INVITED', 'ACTIVE'].includes(row.provision_status)) return 'active';
  return 'pending';
}

const STATUS_STYLES = {
  active:  { label: 'Active',  bg: 'rgba(26,127,78,0.12)',  fg: brand.green },
  pending: { label: 'Pending', bg: 'rgba(208,155,44,0.15)', fg: brand.goldenrod },
  removed: { label: 'Removed', bg: 'rgba(192,57,43,0.12)',  fg: brand.red },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span style={{ background: s.bg, color: s.fg, padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}
StatusBadge.propTypes = { status: PropTypes.string };

const CERT_STYLES = {
  completed:    { label: 'Certified',     bg: 'rgba(26,127,78,0.12)',  fg: brand.green },
  pending:      { label: 'Pending',       bg: 'rgba(208,155,44,0.15)', fg: brand.goldenrod },
  not_started:  { label: 'Not started',   bg: 'rgba(88,88,91,0.10)',   fg: brand.charcoal },
};

function CertBadge({ status }) {
  const s = CERT_STYLES[status] || CERT_STYLES.not_started;
  return (
    <span style={{ background: s.bg, color: s.fg, padding: '3px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}
CertBadge.propTypes = { status: PropTypes.string };

const USAGE_META = {
  using:      { label: 'Active',   icon: 'check-circle-fill', bg: 'rgba(26,127,78,0.12)', fg: brand.green },
  no_account: { label: 'No Usage', icon: 'x-circle',          bg: 'rgba(192,57,43,0.10)', fg: brand.red },
};

function UsageBadge({ status }) {
  const m = USAGE_META[status];
  if (!m) return <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>—</span>;
  return (
    <span title={status === 'using' ? 'Spend in the last 30 days' : 'No spend in the last 30 days'} style={{ background: m.bg, color: m.fg, padding: '3px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}>
      <i className={`bi bi-${m.icon} me-1`} />{m.label}
    </span>
  );
}
UsageBadge.propTypes = { status: PropTypes.string };

function DriftFlag({ row }) {
  if (row.flag_removed_but_active_usage) {
    return (
      <span title="Removed/deactivated in our system, but still showing Claude spend — they likely weren't manually removed from the Anthropic console." style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: brand.red, fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
        <i className="bi bi-exclamation-triangle-fill" /> Not removed from Claude?
      </span>
    );
  }
  if (row.flag_active_no_usage) {
    return (
      <span title="Active and added as a Claude member, but no spend in the last 30 days — may just not have started yet." style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: brand.goldenrod, fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
        <i className="bi bi-question-circle-fill" /> No usage yet
      </span>
    );
  }
  return <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>—</span>;
}
DriftFlag.propTypes = { row: PropTypes.object.isRequired };

const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '—';

export default function B2BClaudeActivations() {
  const { data: rows = [], isLoading } = useListB2BClaudeActivationsQuery();

  const [statusFilter, setStatusFilter] = useState('all');
  const [certFilter, setCertFilter] = useState('all');
  const [firmFilter, setFirmFilter] = useState('all');
  const [flagFilter, setFlagFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const firmOptions = useMemo(
    () => Array.from(new Set(rows.map(r => r.firm_name).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter !== 'all' && memberStatus(r) !== statusFilter) return false;
      if (certFilter !== 'all' && (r.cert_status || 'not_started') !== certFilter) return false;
      if (firmFilter !== 'all' && (r.firm_name || '') !== firmFilter) return false;
      if (flagFilter === 'removed_active' && !r.flag_removed_but_active_usage) return false;
      if (flagFilter === 'active_no_usage' && !r.flag_active_no_usage) return false;
      if (!matchesTier(r.activity_30d, tierFilter)) return false;
      if (q && !`${r.email || ''} ${r.first_name || ''} ${r.last_name || ''} ${r.firm_name || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, statusFilter, certFilter, firmFilter, flagFilter, tierFilter, searchQuery]);

  const stats = useMemo(() => ({
    total:   rows.length,
    active:  rows.filter(r => memberStatus(r) === 'active').length,
    removed: rows.filter(r => memberStatus(r) === 'removed').length,
    flagged: rows.filter(r => r.flag_removed_but_active_usage || r.flag_active_no_usage).length,
  }), [rows]);

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      {/* Header */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: '1.5rem 2rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.65rem', flexShrink: 0 }}>
            <i className="bi bi-building-gear" style={{ color: brand.goldenrod, fontSize: '1.5rem' }} />
          </div>
          <div>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              B2B
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: 0 }}>B2B Claude Activations</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: '4px 0 0', fontSize: '0.85rem' }}>
              Every B2B firm member&apos;s seat status, certification, and live Claude usage — flagging where our records and the Anthropic console may have drifted apart.
            </p>
          </div>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="row g-3 mb-3">
        {[
          { label: 'Total Members', value: stats.total },
          { label: 'Active',        value: stats.active },
          { label: 'Removed',       value: stats.removed },
          { label: 'Flagged',       value: stats.flagged, danger: stats.flagged > 0 },
        ].map(s => (
          <div key={s.label} className="col-md-3">
            <div style={{ background: 'white', borderRadius: '12px', padding: '1rem 1.25rem', boxShadow: '0 2px 12px rgba(10,41,71,0.08)' }}>
              <div style={{ color: brand.charcoal, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{s.label}</div>
              <div style={{ color: s.danger ? brand.red : brand.deepBlue, fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.2 }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {filtered.some(r => r.activity_30d != null) && (
        <div className="mb-3">
          <UsageTierChart
            counts={filtered.map(r => r.activity_30d || 0)}
            title="Usage Distribution"
            subtitle="B2B members grouped into engagement tiers by activity in the last 30 days."
          />
        </div>
      )}

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <i className="bi bi-list-check" style={{ color: brand.goldenrod }} />
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Members</h5>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Showing {filtered.length === rows.length ? rows.length : `${filtered.length} of ${rows.length}`}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search member or firm"
              aria-label="Search"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', width: '190px' }}
            />
            {firmOptions.length > 0 && (
              <select value={firmFilter} onChange={e => setFirmFilter(e.target.value)} aria-label="Filter by firm" title="Firm" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer' }}>
                <option value="all" style={{ color: brand.charcoal }}>All firms</option>
                {firmOptions.map(f => <option key={f} value={f} style={{ color: brand.charcoal }}>{f}</option>)}
              </select>
            )}
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label="Filter by status" title="Status" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer' }}>
              <option value="all"     style={{ color: brand.charcoal }}>All statuses</option>
              <option value="active"  style={{ color: brand.charcoal }}>Active</option>
              <option value="pending" style={{ color: brand.charcoal }}>Pending</option>
              <option value="removed" style={{ color: brand.charcoal }}>Removed</option>
            </select>
            <select value={certFilter} onChange={e => setCertFilter(e.target.value)} aria-label="Filter by cert status" title="Certification" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer' }}>
              <option value="all"         style={{ color: brand.charcoal }}>Any cert</option>
              <option value="completed"   style={{ color: brand.charcoal }}>Certified</option>
              <option value="pending"     style={{ color: brand.charcoal }}>Pending</option>
              <option value="not_started" style={{ color: brand.charcoal }}>Not started</option>
            </select>
            <select value={flagFilter} onChange={e => setFlagFilter(e.target.value)} aria-label="Filter by drift flag" title="Drift flags" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer' }}>
              <option value="all"            style={{ color: brand.charcoal }}>All flags</option>
              <option value="removed_active" style={{ color: brand.charcoal }}>Removed but still active</option>
              <option value="active_no_usage" style={{ color: brand.charcoal }}>Active, no usage</option>
            </select>
            {rows.some(r => r.activity_30d != null) && (
              <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} aria-label="Filter by usage category" title="Usage category" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer' }}>
                <option value="all" style={{ color: brand.charcoal }}>All categories</option>
                {USAGE_TIERS.map(t => <option key={t.key} value={t.key} style={{ color: brand.charcoal }}>{t.label}</option>)}
              </select>
            )}
          </div>
        </div>

        {isLoading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div className="spinner-border" style={{ color: brand.tealBlue }} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: brand.charcoal }}>
            <i className="bi bi-inbox" style={{ fontSize: '2.5rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
            No members match the current filters.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: '0.825rem' }}>
              <thead style={{ background: brand.softGray }}>
                <tr>
                  <th style={{ padding: '0.5rem 0.75rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Member</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Firm</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Status</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Cert</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>Claude Usage</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Flag</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const status = memberStatus(r);
                  const fullName = `${r.first_name || ''} ${r.last_name || ''}`.trim();
                  return (
                    <tr key={r.uuid} style={r.flag_removed_but_active_usage ? { background: 'rgba(192,57,43,0.05)' } : undefined}>
                      <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, color: brand.deepBlue, whiteSpace: 'nowrap' }}>{fullName || r.email}</span>
                          <TierBadge count={r.activity_30d} />
                        </div>
                        {fullName && <div style={{ color: brand.charcoal, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{r.email}</div>}
                      </td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle', color: brand.charcoal, whiteSpace: 'nowrap' }}>{r.firm_name || '—'}</td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}><StatusBadge status={status} /></td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}>
                        <CertBadge status={r.cert_status} />
                        {r.cert_submitted_at && <div style={{ color: brand.charcoal, fontSize: '0.68rem', marginTop: 2 }}>{fmtDate(r.cert_submitted_at)}</div>}
                      </td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}><UsageBadge status={r.claude_usage_status} /></td>
                      <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle' }}><DriftFlag row={r} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
